import type { DeviceToRelay, Payload, RelayToDevice } from '@relyo/protocol';
import { HEARTBEAT_INTERVAL_MS, RECONNECT_BACKOFF_MS } from './constants';

export type OutgoingType = DeviceToRelay['t'];
export type OutgoingPayload<T extends OutgoingType> = Payload<DeviceToRelay, T>;
export type HelloPayload = OutgoingPayload<'device.hello'>;

export interface ConnectionHandlers {
  /** Liefert die aktuelle `device.hello`-Payload (bei jedem Connect neu). */
  hello(): HelloPayload;
  /** Socket offen und `device.hello` gesendet – weitere Nachrichten dürfen folgen. */
  onReady(): void;
  onMessage(msg: RelayToDevice): void;
  /** Socket geschlossen (danach ggf. automatischer Reconnect). */
  onClose(): void;
}

let counter = 0;
function messageId(): string {
  counter = (counter + 1) % 0x7fffffff;
  return `${Date.now().toString(36)}-${counter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * `wss://relyo.de` → `wss://relyo.de/ws/device?projectKey=…` (http/https → ws/wss). Ohne Key
 * (offener Dienst) entfällt der Query-Parameter.
 */
export function deviceSocketUrl(relayUrl: string, projectKey: string | null): string {
  const base = relayUrl
    .trim()
    .replace(/\/+$/, '')
    .replace(/^http(s?):\/\//i, 'ws$1://');
  if (!projectKey) return `${base}/ws/device`;
  return `${base}/ws/device?projectKey=${encodeURIComponent(projectKey)}`;
}

function isRelayMessage(v: unknown): v is RelayToDevice {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as { t?: unknown; p?: unknown };
  return typeof o.t === 'string' && typeof o.p === 'object' && o.p !== null;
}

/**
 * Device-WebSocket mit Hello, Heartbeat (20 s) und Reconnect-Backoff 1/2/4/8 s.
 * Reconnects passieren nur, solange die Verbindung gewünscht ist (`open()` ohne `close()`).
 */
export class Connection {
  private ws: WebSocket | null = null;
  private wanted = false;
  private ready = false;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly url: string,
    private readonly handlers: ConnectionHandlers,
  ) {}

  get isReady(): boolean {
    return this.ready;
  }

  /** Verbindung gewünscht (offen oder im Aufbau/Backoff). */
  get isWanted(): boolean {
    return this.wanted;
  }

  open(): void {
    this.wanted = true;
    if (this.ws || this.reconnectTimer !== null) return;
    this.connectNow();
  }

  close(): void {
    this.wanted = false;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.attempt = 0;
    const ws = this.ws;
    this.teardown();
    if (ws) {
      try {
        ws.close(1000, 'client_close');
      } catch {
        // ignorieren
      }
    }
  }

  /** Sendet eine Nachricht; gibt die Nachrichten-ID zurück oder `null`, wenn nicht verbunden. */
  send<T extends OutgoingType>(t: T, p: OutgoingPayload<T>): string | null {
    const ws = this.ws;
    if (!this.ready || !ws || ws.readyState !== 1 /* OPEN */) return null;
    return this.sendRaw(ws, t, p);
  }

  private sendRaw(ws: WebSocket, t: string, p: unknown): string | null {
    const id = messageId();
    try {
      ws.send(JSON.stringify({ t, id, ts: Date.now(), p }));
      return id;
    } catch {
      return null;
    }
  }

  private connectNow(): void {
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.sendRaw(ws, 'device.hello', this.handlers.hello());
      this.ready = true;
      this.heartbeatTimer = setInterval(() => {
        this.send('device.heartbeat', {});
      }, HEARTBEAT_INTERVAL_MS);
      this.handlers.onReady();
    };
    ws.onmessage = (ev: MessageEvent) => {
      if (this.ws !== ws || typeof ev.data !== 'string') return;
      let data: unknown;
      try {
        data = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (!isRelayMessage(data)) return;
      if (data.t === 'device.ack') this.attempt = 0;
      this.handlers.onMessage(data);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.teardown();
      this.handlers.onClose();
      if (this.wanted) this.scheduleReconnect();
    };
    ws.onerror = () => {
      // `close` folgt immer; dort wird reagiert.
    };
  }

  private teardown(): void {
    const ws = this.ws;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
    }
    this.ws = null;
    this.ready = false;
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private scheduleReconnect(): void {
    if (!this.wanted || this.reconnectTimer !== null) return;
    const delay =
      RECONNECT_BACKOFF_MS[Math.min(this.attempt, RECONNECT_BACKOFF_MS.length - 1)] ?? 8_000;
    this.attempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wanted) this.connectNow();
    }, delay);
  }
}
